import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trophy, MessageCircle, ArrowRight } from "lucide-react";
import { ShareIcon } from "./icons/ShareIcon";

interface WorkoutCompletionModalProps {
  coachName?: string;
  isOpen: boolean;
  onClose: () => void;
  onAnalyzeNow: () => void;
  onAnalyzeLater: () => void;
  onShare?: () => void;
  workoutTitle: string;
  duration: number;
  completedSets?: number;
  averagePace?: string;
  distance?: number;
}

const motivationalMessages = [
  "Outstanding work! You've conquered another workout!",
  "Incredible dedication! You're building strength with every rep!",
  "Phenomenal effort! Your commitment is paying off!",
  "Amazing job! You've pushed through and achieved greatness!",
  "Exceptional workout! Your determination is inspiring!",
  "Fantastic effort! You're getting stronger every day!",
  "Brilliant session! Your hard work is transforming you!",
  "Superb dedication! You've earned this victory!"
];

export function WorkoutCompletionModal({
  isOpen,
  onClose,
  onAnalyzeNow,
  onAnalyzeLater,
  onShare,
  workoutTitle,
  duration,
  completedSets,
  averagePace,
  distance
}: WorkoutCompletionModalProps) {
  const randomMessage = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
  const isRunning = averagePace !== undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-w-[calc(100vw-2rem)] bg-background border-2 border-primary/30 shadow-2xl p-4 sm:p-6" hideClose>
        <DialogHeader className="text-center space-y-2 sm:space-y-4 pb-0">
          <div className="mx-auto w-14 h-14 sm:w-20 sm:h-20 bg-gradient-primary rounded-full flex items-center justify-center shadow-glow">
            <Trophy className="h-7 w-7 sm:h-10 sm:w-10 text-gold" />
          </div>
          <DialogTitle className="text-lg sm:text-2xl font-bold text-center text-foreground">
            Workout Complete!
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 sm:space-y-5 py-2 sm:py-3">
          <div className="text-center">
            <p className="text-sm sm:text-lg font-semibold text-foreground mb-2 sm:mb-4 leading-snug px-1">
              {randomMessage}
            </p>
            
            <div className="grid grid-cols-2 gap-2 sm:gap-4 text-sm bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg sm:rounded-xl p-3 sm:p-5 border border-primary/30">
              <div className="text-center">
                <div className="font-bold text-sm sm:text-lg text-foreground mb-0.5">{workoutTitle}</div>
                <div className="text-xs text-muted-foreground font-medium">Workout</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-sm sm:text-lg text-foreground mb-0.5">{duration} min</div>
                <div className="text-xs text-muted-foreground font-medium">Duration</div>
              </div>
              <div className="text-center col-span-2 mt-1 sm:mt-2 pt-2 sm:pt-3 border-t border-primary/40">
                {isRunning ? (
                  <>
                    <div className="font-bold text-base sm:text-xl text-primary mb-0.5">{averagePace}</div>
                    <div className="text-xs text-muted-foreground font-medium">Average Pace</div>
                    {distance && (
                      <div className="mt-2">
                        <div className="font-bold text-sm sm:text-base text-foreground">{distance} km</div>
                        <div className="text-xs text-muted-foreground font-medium">Distance</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="font-bold text-base sm:text-xl text-primary mb-0.5">{completedSets} sets</div>
                    <div className="text-xs text-muted-foreground font-medium">Completed</div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2 sm:space-y-3 px-0 sm:px-1">
            <p className="text-center text-xs sm:text-base font-medium text-foreground">
              Want your AI coach to analyze your workout performance?
            </p>
            
            <div className="flex flex-col gap-2">
              <Button 
                onClick={onAnalyzeNow}
                className="w-full gap-2 h-10 sm:h-11 text-xs sm:text-sm font-semibold bg-gradient-primary text-primary-foreground hover:opacity-90"
              >
                <MessageCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Analyze Now
              </Button>
              
              <Button 
                variant="outline" 
                onClick={onAnalyzeLater}
                className="w-full gap-2 h-10 sm:h-11 text-xs sm:text-sm font-semibold border-2"
              >
                <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Maybe Later
              </Button>
              
              {onShare && (
                <Button 
                  variant="ghost" 
                  onClick={onShare}
                  className="w-full gap-2 h-10 sm:h-11 text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <ShareIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                  Share Workout
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}